import React from 'react';
import classNameBind from 'classnames/bind';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import Icon from '../Icon';
import Label from '../Label';
import Popover from '../Popover';
import Button from '../Button';
import styles from './FilteredSelect.module.css';
import { FilteredSelectInner } from './components/FilteredSelectInner';
import { KEYS } from '../../utils/methods/keyboardKeys';

const bindClasses = classNameBind.bind(styles);

const flatten = arr => [].concat(...arr);

class FilteredSelect extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selected: props.value,
    };
  }

  forceClosePopover = () => {};

  getItemsAsList = () => {
    const { options } = this.props;

    return flatten(
      options.map(elem => elem.items),
    );
  }

  getSelectedId = () => {
    const { value } = this.props;
    const { selected } = this.state;
    const selectedValue = selected || value;

    return get(this.getItemsAsList().find(item => isEqual(item.value, selectedValue)), 'id');
  }

  isInsideTheRange = (index, items) => (
    index >= 0 && index < items.length
  )

  handleKeyDown = (event, onToggle, isOpen) => {
    const { value, onOpen } = this.props;
    const items = this.getItemsAsList();
    const { selected } = this.state;
    const selectedValue = selected || value;

    let index = items.findIndex(item => isEqual(item.value, selectedValue));

    if (
      (((event.key === KEYS.TAB || event.key === KEYS.ESC) && isOpen)
      || (!isOpen && event.key === KEYS.SPACE)) && onToggle) {
      // open/close popover on tab/space
      event.preventDefault();
      onToggle();

      if (!isOpen && onToggle) {
        onOpen(event);
      }
    }

    if (event.key === KEYS.ENTER && onToggle) { // clear search, close popover and submit
      event.preventDefault();
      this.handleChange(items[index]);
      onToggle();
    }

    if ([KEYS.DOWN, KEYS.UP].includes(event.key)) { // move between the items
      event.preventDefault();

      if (!isOpen && onToggle) {
        onToggle();
      }

      if (event.key === KEYS.DOWN) {
        do { // skip disabled items
          index += 1;
        } while (items[index].disabled === true);
      } else if (event.key === KEYS.UP) {
        do {
          index -= 1;
        } while (items[index].disabled === true);
      }

      if (this.isInsideTheRange(index, items)) {
        this.setState({
          selected: items[index].value,
        });
      }
    }

    return null;
  }

  handleSearchChange = (event, isOpen) => {
    const { onSearch } = this.props;

    if (isOpen) {
      onSearch({
        target: {
          value: event.target.value,
        },
      });
    }
  }

  handleChange = (newValue) => {
    const { value, onChange, name } = this.props;

    if (!isEqual(newValue.value, value)) {
      onChange({
        target: {
          name,
          value: newValue.value,
        },
      });
    }
  }

  render() {
    const {
      getSelectedName,
      getSelectedCaption,
      value,
      onOpen,
      disabled,
      zeroState,
      search,
      t,
      icon,
      error,
      touched,
      isLoading,
      dataId,
      options,
      onScrolledDown,
      isLoadingLazy,
      fixedWidth,
      position,
      renderAfterListElement,
      wordBreakingStyle,
      speechBubble,
      searchPlaceholder,
      customActivator,
      onClear,
      size,
    } = this.props;

    const zeroStateText = zeroState || t('selectValue');
    const itemsCount = this.getItemsAsList().length;
    const selectedCaption = getSelectedCaption(value);

    return (
      <div className={bindClasses('wrapper')} data-ppuc-id={dataId}>
        <div className={bindClasses('inner-wrapper', { disableFocusStyle: !!customActivator })}>
          <Popover
            onOpen={onOpen}
            position={position}
            speechBubble={speechBubble}
            disableFocusStyle={!!customActivator}
            activatorElement={customActivator || (
              <div
                ref={this.listRef}
                className={bindClasses('filtered-select', { disabled, error: error && touched, withOnClear: onClear }, size)}
                data-ppuc-id={`${dataId}.button`}
              >
                {icon && (
                <div className={bindClasses('icon-wrapper')} data-ppuc-id={`${dataId}.icon`}>
                  <Icon name={icon} />
                </div>
                )}
                <div className={bindClasses('flex-text')}>
                  <span className={bindClasses('label')} data-ppuc-id={`${dataId}.value`}>
                    <Label noSelect>
                      {getSelectedName(value) || zeroStateText}
                    </Label>
                  </span>
                  {selectedCaption && <div className={bindClasses('caption')}>{selectedCaption}</div>}
                </div>
                {!speechBubble && (
                <Icon
                  name="chevron-down"
                  className={bindClasses('icon-arrow')}
                  tabIndex={-1}
                />
                )}
              </div>
            )}
            fixedWidth={fixedWidth}
            childToggle={{ onItemSelected: () => true, forceClosePopover: () => true }}
            disabled={disabled}
            noPadding
            forceRecalculateOnSafari
            onKeyDown={this.handleKeyDown}
            focusOrder={['content']}
            dataId={`${dataId}.popover`}
          >
            <FilteredSelectInner
              groups={itemsCount === 0 ? [] : options}
              onItemSelected={this.handleChange}
              search={search}
              onSearchChange={this.handleSearchChange}
              onSearchKeyDown={this.handleKeyDown}
              isLoading={isLoading}
              current={this.getSelectedId()}
              dataId={`${dataId}.contextMenu`}
              onScrolledDown={onScrolledDown}
              isLoadingLazy={isLoadingLazy}
              renderAfterListElement={renderAfterListElement}
              forceClosePopover={this.forceClosePopover}
              wordBreakingStyle={wordBreakingStyle}
              searchPlaceholder={searchPlaceholder}
            />
          </Popover>
        </div>
        {onClear && (
          <div className={bindClasses('clear-button-wrapper')}>
            <Button
              icon="cross"
              onClick={onClear}
              size={size}
              dataId={`${dataId}.onClearButton`}
            />
          </div>
        )}
      </div>
    );
  }
}

FilteredSelect.defaultProps = {
  value: null,
  search: '',
  zeroState: '',
  disabled: false,
  onOpen: () => {},
  onChange: () => {},
  onSearch: () => {},
  onScrolledDown: () => {},
  isLoading: false,
  isLoadingLazy: false,
  dataId: 'FilteredSelect',
  fixedWidth: true,
  position: 'bottom',
  icon: '',
  speechBubble: false,
  renderAfterListElement: () => {},
  getSelectedCaption: () => {},
  wordBreakingStyle: 'break-all',
  searchPlaceholder: '',
};

export { FilteredSelect };
